import { Close } from '@mui/icons-material';
import { IconButton, TextField, Typography } from '@mui/material';
import CurrencyRate from 'components/CurrencyRate';
import OutlinedAccordion from 'components/OutlinedAccordion';
import AllocationsManager from 'components/new/AllocationsManager';
import BasicSelect from 'further-ui/components/BasicSelect';
import FormField from 'further-ui/components/FormField';
import { ActionCommitmentStatus } from 'further-types/lp/action';
import { FundingSource } from 'further-types/lp/investment';
import useFundingSourceOptions from 'hooks/form/useFundingSourceOptions';
import { useLayoutContext } from 'contexts/LayoutContext';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { Column, Row } from 'further-ui/components/Layout';
import NumberInput from 'further-ui/components/NumberInput';
import StaticField from 'further-ui/components/StaticField';
import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';
import useCurrencyRate from 'hooks/lp/data/currencies/useCurrencyRate';
import api from 'lib/trpcClient';
import { round } from 'lodash';
import { BankFee } from './ReviewContent';

type Props = {
  vehicleId: string;
  bankFee: BankFee;
  setBankFee: React.Dispatch<React.SetStateAction<BankFee>>;
  currency: CurrencyCode;
  date: CalendarDay;
  onRemove: () => void;
};

const AddBankFeeForm: React.FC<Props> = ({
  vehicleId,
  bankFee,
  setBankFee,
  currency,
  date,
  onRemove,
}) => {
  const { vehicle } = useLayoutContext();
  const [typeOptions] = api.incomeAndExpenses.getTypeOptions.useSuspenseQuery({
    vehicleId,
  });
  const amountExVat = round(bankFee.amount / (1 + bankFee.vatRate / 100), 2);

  const fundingSourceOptions = useFundingSourceOptions(vehicleId);

  const commitmentStatusOptions = [
    {
      value: ActionCommitmentStatus.InsideCommitment,
      label: 'Inside commitment',
    },
    {
      value: ActionCommitmentStatus.OutsideCommitment,
      label: 'Outside commitment',
    },
  ];

  const { isFetchingRate, fxRate, changeRate } = useCurrencyRate({
    vehicleId,
    currency,
    date,
    defaultCurrencyRate: bankFee.currencyRate,
    onRateChange: (rate) =>
      setBankFee((prev) => ({ ...prev, currencyRate: rate })),
  });

  const isMultiCurrency = vehicle && currency !== vehicle.baseCurrency;

  return (
    <Column spacing="lg">
      <Row centered justify="space-between">
        <Typography variant="h5">Add bank fee</Typography>
        <IconButton size="small" onClick={onRemove}>
          <Close fontSize="small" />
        </IconButton>
      </Row>
      <Row spacing="md" fullWidth>
        <FormField label="Expense type" size="md" spacing="xs">
          <BasicSelect
            fullWidth
            options={typeOptions.expenseTypeOptions.map((opt) => ({
              value: opt.customActionTypeId ?? opt.value,
              label: opt.label,
            }))}
            value={bankFee.customActionTypeId ?? ''}
            onChange={(value) => {
              const selected = typeOptions.expenseTypeOptions.find(
                (opt) => (opt.customActionTypeId ?? opt.value) === value,
              );
              setBankFee((prev) => ({
                ...prev,
                customActionTypeId: value || null,
                customActionTypeName: selected?.label ?? null,
              }));
            }}
            placeholder="Select expense type"
          />
        </FormField>
        <FormField
          label="Funding source"
          size="md"
          spacing="xs"
          tooltip="Select whether this action will be paid through general working capital on account or fresh capital that will be part of a future capital call."
        >
          <BasicSelect
            fullWidth
            options={fundingSourceOptions.options}
            value={bankFee.fundingSource ?? ''}
            onChange={(value) =>
              setBankFee((prev) => ({
                ...prev,
                fundingSource: (value as FundingSource) || null,
              }))
            }
            disabled={fundingSourceOptions.isLoading}
            placeholder="Select funding source"
          />
        </FormField>
        <FormField label="Inside/outside commitment" size="md" spacing="xs">
          <BasicSelect
            fullWidth
            options={commitmentStatusOptions}
            value={bankFee.commitmentStatus ?? ''}
            onChange={(value) => {
              const commitmentStatus =
                (value as ActionCommitmentStatus) || null;
              setBankFee((prev) => ({
                ...prev,
                commitmentStatus,
                ...(commitmentStatus ===
                ActionCommitmentStatus.OutsideCommitment
                  ? { fundingSource: FundingSource.CapitalCall }
                  : {}),
              }));
            }}
            placeholder="Select inside/outside commitment"
          />
        </FormField>
      </Row>
      <Row spacing="md" fullWidth>
        <Column spacing="xs" fullWidth>
          <Typography variant="caption" color="textSecondary">
            Amount
          </Typography>
          <NumberInput
            value={bankFee.amount}
            onChange={(amount) =>
              setBankFee((prev) => ({ ...prev, amount: amount ?? 0 }))
            }
            min={0}
            currency={currency}
            currencyStartAdornment
            decimals={2}
            fixedDecimalScale
            fullWidth
            clearZeroValueOnFocus
          />
        </Column>
        <StaticField label="Date">
          <Typography variant="body2">{date.toReadable()}</Typography>
        </StaticField>
      </Row>
      <Row spacing="md" fullWidth>
        <Column spacing="xs" fullWidth>
          <Typography variant="caption" color="textSecondary">
            VAT rate (%)
          </Typography>
          <NumberInput
            value={bankFee.vatRate}
            onChange={(rate) =>
              setBankFee((prev) => ({ ...prev, vatRate: rate ?? 0 }))
            }
            min={0}
            max={100}
            clearZeroValueOnFocus
            fullWidth
          />
          <Typography variant="caption" color="textSecondary">
            Amount ex VAT: {numberToCurrencyString(amountExVat, { currency })}
          </Typography>
        </Column>
        <Column spacing="xs" fullWidth>
          <Typography variant="caption" color="textSecondary">
            Description (optional)
          </Typography>
          <TextField
            size="small"
            variant="outlined"
            fullWidth
            value={bankFee.description}
            onChange={(e) =>
              setBankFee((prev) => ({ ...prev, description: e.target.value }))
            }
          />
        </Column>
      </Row>
      {isMultiCurrency && vehicle && (
        <CurrencyRate
          currency={currency}
          date={date}
          vehicle={vehicle}
          fxRate={fxRate}
          isFetchingRate={isFetchingRate}
          onRateChange={changeRate}
        />
      )}
      <OutlinedAccordion title="Allocations">
        <AllocationsManager
          vehicleId={vehicleId}
          allocations={bankFee.allocations}
          allocatedActionLabel="Expense"
          allocatedAmountTcy={bankFee.amount}
          currency={currency}
          baseCurrency={vehicle?.baseCurrency ?? CurrencyCode.GBP}
          allocationRule={bankFee.allocationRule}
          onAllocationRuleChange={(rule) =>
            setBankFee((prev) => ({ ...prev, allocationRule: rule }))
          }
          onAllocationsChange={(allocs) =>
            setBankFee((prev) => ({
              ...prev,
              allocations: allocs,
            }))
          }
          date={date}
        />
      </OutlinedAccordion>
    </Column>
  );
};

export default AddBankFeeForm;
